import React, { useEffect, useState } from "react";
import { Form, Input, Select } from "antd";
import PhoneInput from "react-phone-number-input";
import { capitalize, isEmpty } from "lodash";
import { DEFAULT_COUNTRY, ServiceTypes } from "../../../config";
import useLang from "../../../hooks/useLang";
import { setDrawerLoading } from "../../../library/drawerLoadingService";

// Service Provider constants
const SERVICE_PROVIDERS = {
  TWILIO: "TWILIO",
  TELNYX: "TELNYX",
};

const SERVICE_PROVIDER_OPTIONS = [
  { value: SERVICE_PROVIDERS.TWILIO, label: "Twilio" },
  { value: SERVICE_PROVIDERS.TELNYX, label: "Telnyx" },
];

const PhoneBotForm = ({
  editablePhone = null,
  saveData,
  locations,
  onSave,
  defaultLocationId = null,
}) => {
  const [lang] = useLang();
  const [form] = Form.useForm();
  const [selectedProvider, setSelectedProvider] = useState(SERVICE_PROVIDERS.TWILIO);

  // Set initial values when editablePhone changes
  useEffect(() => {
    if (editablePhone) {
      const provider = editablePhone?.phoneServiceProvider || SERVICE_PROVIDERS.TWILIO;
      setSelectedProvider(provider);

      form.setFieldsValue({
        phoneServiceProvider: provider,
        serviceName: editablePhone?.serviceName || "",
        phone: editablePhone?.user?.phone || "",
        accountSid: String(editablePhone?.accountSid || ""),
        authToken: String(editablePhone?.authToken || ""),
        messagingProfileId: String(editablePhone?.messagingProfileId || ""),
        voiceApiKey: String(editablePhone?.voiceApiKey || ""),
        voiceApiSecret: String(editablePhone?.voiceApiSecret || ""),
        voiceTwimlSid: String(editablePhone?.voiceTwimlSid || ""),
        type: editablePhone?.type,
        locationId: +editablePhone?.locationId || undefined,
        description: editablePhone?.description || "",
      });
    } else {
      // Reset form for new phone bot
      form.resetFields();
      setSelectedProvider(SERVICE_PROVIDERS.TWILIO);

      // Set default values for new phone bot
      // Use prop defaultLocationId if provided, otherwise use first location
      const initialLocationId =
        defaultLocationId || ((locations || []).length > 0 ? locations[0].id : undefined);
      form.setFieldsValue({
        phoneServiceProvider: SERVICE_PROVIDERS.TWILIO,
        locationId: initialLocationId ? +initialLocationId : undefined,
      });
    }
  }, [editablePhone, locations, form, defaultLocationId]);

  // Handle service provider change
  const handleServiceProviderChange = value => {
    setSelectedProvider(value);
    // Clear provider-specific fields when switching
    if (value === SERVICE_PROVIDERS.TWILIO) {
      form.setFieldsValue({
        voiceApiKey: undefined,
        voiceApiSecret: undefined,
      });
    } else if (value === SERVICE_PROVIDERS.TELNYX) {
      form.setFieldsValue({
        accountSid: undefined,
        authToken: undefined,
        voiceTwimlSid: undefined,
      });
    }
  };

  const handleUpdateClick = async () => {
    try {
      // Validate all form fields
      const values = await form.validateFields();

      setDrawerLoading(true);
      try {
        const savePayload = {
          id: parseInt(editablePhone?.id) || null,
          serviceName: values.serviceName,
          phone: values.phone,
          type: values.type,
          description: values.description || "",
          locationId: values.locationId,
          phoneServiceProvider: values.phoneServiceProvider,
          accountSid: values.accountSid || "TELNYX",
          authToken: values.authToken || "TELNYX",
          messagingProfileId: values.messagingProfileId || null,
          voiceApiKey: values.voiceApiKey || null,
          voiceApiSecret: values.voiceApiSecret || null,
          voiceTwimlSid: values.voiceTwimlSid || null,
          extraData: null,
        };

        await saveData(savePayload);
        return true;
      } catch (error) {
        console.error("Error saving phone bot:", error);
        return false;
      } finally {
        setDrawerLoading(false);
      }
    } catch (errorInfo) {
      // Validation failed
      return false;
    }
  };

  // Expose handleUpdateClick via onSave ref
  useEffect(() => {
    if (onSave && onSave.current !== undefined) {
      onSave.current.handleSubmit = handleUpdateClick;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSave]);

  return (
    <Form form={form} layout="vertical" className="relative">
      {/* Service Provider Selection */}
      <Form.Item
        name="phoneServiceProvider"
        label="Service provider"
        rules={[
          {
            required: true,
            message: "Please select a service provider",
          },
        ]}
      >
        <Select
          placeholder="Select service provider"
          showArrow
          onChange={handleServiceProviderChange}
        >
          {SERVICE_PROVIDER_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <div className="flex gap-3 w-full">
        <Form.Item
          name="serviceName"
          label={capitalize(lang.serviceName)}
          className="basis-1/2"
          rules={[
            {
              required: true,
              message: capitalize(lang.enterServiceName),
            },
            {
              max: 50,
              message: "Service name cannot exceed 50 characters",
            },
          ]}
        >
          <Input placeholder={capitalize(lang.serviceName)} maxLength={50} />
        </Form.Item>

        <Form.Item
          name="phone"
          label={lang.phoneNumber}
          className="basis-1/2"
          rules={[
            {
              required: true,
              message: lang.enterPhoneNumber,
            },
          ]}
        >
          <PhoneInput
            international
            withCountryCallingCode
            countryCallingCodeEditable={false}
            defaultCountry={DEFAULT_COUNTRY}
            placeholder={lang.phoneNumber}
            className="phoneInputCss"
          />
        </Form.Item>
      </div>

      {/* Conditional fields based on service provider */}
      {selectedProvider === SERVICE_PROVIDERS.TWILIO && (
        <>
          <Form.Item
            name="accountSid"
            label={lang.accountSid}
            rules={[
              {
                required: true,
                message: lang.enterAccountSid,
              },
            ]}
          >
            <Input placeholder={lang.accountSid} />
          </Form.Item>

          <Form.Item
            name="authToken"
            label={capitalize(lang.authToken)}
            rules={[
              {
                required: true,
                message: capitalize(lang.enterAuthToken),
              },
            ]}
          >
            <Input placeholder={capitalize(lang.authToken)} />
          </Form.Item>

          <Form.Item
            name="messagingProfileId"
            label={
              selectedProvider === SERVICE_PROVIDERS.TELNYX
                ? "Messaging profile id"
                : "Messaging service id"
            }
            rules={[
              {
                required: selectedProvider === SERVICE_PROVIDERS.TELNYX,
                message: `Please enter ${
                  selectedProvider === SERVICE_PROVIDERS.TELNYX
                    ? "Messaging Profile ID"
                    : "Messaging Service ID"
                }`,
              },
            ]}
          >
            <Input
              placeholder={
                selectedProvider === SERVICE_PROVIDERS.TELNYX
                  ? "Messaging profile id"
                  : "Messaging service id"
              }
            />
          </Form.Item>

          <Form.Item name="voiceTwimlSid" label="Voice TwiML SID">
            <Input placeholder="Voice TwiML SID" />
          </Form.Item>
        </>
      )}

      {selectedProvider === SERVICE_PROVIDERS.TELNYX && (
        <>
          <Form.Item
            name="messagingProfileId"
            label="Messaging Profile ID"
            rules={[
              {
                required: true,
                message: "Please enter Messaging Profile ID",
              },
            ]}
          >
            <Input placeholder="Messaging Profile ID" />
          </Form.Item>

          {/* <Form.Item name="voiceApiKey" label="Voice API Key">
            <Input placeholder="Voice API Key" />
          </Form.Item>

          <Form.Item name="voiceApiSecret" label="Voice API Secret">
            <Input placeholder="Voice API Secret" />
          </Form.Item> */}
        </>
      )}

      <Form.Item
        name="type"
        label={lang.type}
        rules={[
          {
            required: true,
            message: lang.selectType,
          },
        ]}
      >
        <Select placeholder={capitalize(lang.selectTypeLabel)} showArrow>
          {ServiceTypes.map(e => (
            <Select.Option key={e.value} value={e.value}>
              {e.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="locationId"
        label={lang.location}
        rules={[
          {
            required: true,
            message: lang.plzSelectLocation,
          },
        ]}
      >
        <Select disabled={!isEmpty(editablePhone)} placeholder={lang.selectLocation} showArrow>
          {(locations || []).map(item => (
            <Select.Option key={item?.id} value={+item?.id}>
              {item.title}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="description" label={lang.description}>
        <Input.TextArea placeholder={lang.description} rows={4} maxLength={300} />
      </Form.Item>
    </Form>
  );
};

export default PhoneBotForm;
